import { EventEmitter } from "./event-emitter";

class ScannerListenerClass{
	private readonly maxDelayBetweenKeys:number = 40.0;
	private readonly maxInputLength:number = 50;
	private emitter:EventEmitter = new EventEmitter();

	private input:string = "";
	private altBuffer:string = "";
	private timeout:number = -1;
	private altHasBeenPressed = false;

	private activeElementInputValue:string = "";
	private activeElement:HTMLInputElement | null = null;

	public readonly AFTER_SCAN_CLEAR_ACTIVE_INPUIT_TIMEOUT = 50;

	public constructor(){
		this.onKey = this.onKey.bind(this);
		this.onKeyUp = this.onKeyUp.bind(this);
	}

	// Check if this class has enough characters in its input buffer
	public couldFireSoon():boolean{
		return this.input.length > 2;
	}


	private startListening(){
		window.addEventListener("keydown", this.onKey, true);
		window.addEventListener("keyup", this.onKeyUp, true);
	}

	private stopListening(){
		window.removeEventListener("keydown", this.onKey, true);
		window.removeEventListener("keyup", this.onKeyUp, true);
	}

	private onKey(event:KeyboardEvent){
		if((event.target as HTMLInputElement).name=="description" ||
			(event.target as HTMLInputElement).name=="wide-description" ||
			(event.target as HTMLInputElement).name=="unit"
		){
			return;
		}
		if (this.input == "" && !this.altHasBeenPressed){
			if (document.activeElement && document.activeElement.tagName.toLowerCase() == "input") {
				this.activeElement = (document.activeElement as HTMLInputElement);
				this.activeElementInputValue = this.activeElement.value;
			}
		}
		if (event.key == "Alt") {
			this.altHasBeenPressed = true;
		}
		if (event.ctrlKey) return;
		if (event.altKey) {
			this.onAltKey(event);
		} else if (event.key == "Enter" || event.key == "PageDown") {
			return this.onEnter(event);
		} else if (event.key.length == 1) {
			this.onNormalKeyPress(event.key);
		}
		this.clearTimeout();
		this.setTimeout();
	}

	private onKeyUp(event:KeyboardEvent){
		// Event.key=="alt" does not work in chromium based browsers
		if (event.key == "Alt" || event.code == "AltLeft" || event.code == "AltRight") {
			if (this.altBuffer.length == 0) return;
			let num = parseInt(this.altBuffer);
			this.altBuffer = "";
			if (Number.isNaN(num)) return;
			let char = String.fromCharCode(num);
			this.onNormalKeyPress(char);
		}else if (event.key == "PageDown") { // For some reason page down doesn't fire in keydown events so I put it here
			return this.onEnter(event);
		}
	}

	private onAltKey(event:KeyboardEvent){
		if (event.key.length > 1) return;
		this.clearTimeout();
		if (this.input.length > this.maxInputLength) {
			this.input = "";
			this.altHasBeenPressed = false;
			return;
		}
		this.altBuffer += event.key;
	}

	private clearTimeout(){
		if (this.timeout != -1) {
			clearTimeout(this.timeout);
			this.timeout = -1;
		}
	}

	private setTimeout(){
		this.timeout = setTimeout(()=>{
			this.input = "";
			this.altHasBeenPressed = false;
			this.activeElementInputValue = "";
			this.timeout = -1;
		}, this.maxDelayBetweenKeys);
	}

	private onNormalKeyPress(key:string){
		this.input += key;
		if (this.input.length > this.maxInputLength) {
			this.activeElementInputValue = "";
			this.activeElement = null;
			this.input = "";
			this.altHasBeenPressed = false;
			return;
		}
	}

	private onEnter(event:KeyboardEvent){
		if (this.input != "") {
			if (this.activeElement) {
				this.activeElement.value = this.activeElementInputValue;
				let event = new Event("input", {bubbles: true, cancelable: true});
				this.activeElement.dispatchEvent(event);
			}
		}
		this.clearTimeout();
		setTimeout(()=>{
			if (this.input.length > 2){
				this.clearActiveElement(event);
				let value = this.input;
				if (value.endsWith("%")) {
					value = value.slice(0, -1);
				}
				this.emitter.emit("input", value);
				this.input = "";
				this.altHasBeenPressed = false;
			}
			this.setTimeout();
		}, this.AFTER_SCAN_CLEAR_ACTIVE_INPUIT_TIMEOUT);
	}

	private clearActiveElement(event:KeyboardEvent){
		event.stopPropagation();
		event.preventDefault();
		let activeElement = document.activeElement as HTMLInputElement;
		if (!activeElement) return;
		if (!activeElement.blur) return;
		if (activeElement.tagName.toLowerCase() != "input") return;
		let val = activeElement.value;
		if (val.length == 0) return;
		if (val.length < this.input.length){
			activeElement.value = "";
			return;
		}
		val = val.substring(0, val.length - this.input.length);
		let inputEvent = new InputEvent("input", {data: val});
		activeElement.value = val;
		activeElement.dispatchEvent(inputEvent);
		requestAnimationFrame(()=>{
			activeElement.blur();
		});
	}

	public emulate(value:string){
		this.emitter.emit("input", value);
	}

	public onInput(handler:(text:string)=>void){
		if (!this.emitter.hasSubs()){
			this.startListening();
		}
		this.emitter.on("input", handler);
	}

	public offInput(handler:(text:string)=>void){
		this.emitter.off("input", handler);
		if (!this.emitter.hasSubs()){
			this.stopListening();
		}
	}

}

export const ScannerListener = new ScannerListenerClass();